import { gql } from '@apollo/client';
import { YieldAggregatesEconFragment } from '../../queries';
import { MULTI_YEAR, N_YEARS } from '../../constants/years';
import { asQuantity } from '../../utils/formatters';
import { useUnitsMap } from '../../contexts/UnitContext';
import { YIELD_UNITS_BY_DEFAULT } from '../../constants/units';
import ExportDashboardYearOrYearsButton, {
  sliceYearArray,
} from './ExportDashboardYearOrYearsButton';
import { getYieldData } from '../../utils/transforms';
import yieldMetrics from '../../constants/yieldMetrics';

const yieldQuery = gql`
  ${YieldAggregatesEconFragment}
  query ProjectListYieldQuery($ids: [ID!]) {
    farms(ids: $ids) {
      id
      name
      startYear
      cropAggregates {
        crop {
          id
          name
        }
        yieldAggregates {
          ...YieldAggregateFields
        }
      }
    }
  }
`;

const yieldColumns = (year, years, startYear) => [
  { displayString: 'Farm Name', accessor: 'farmName' },
  { displayString: 'Crop', accessor: (row) => row?.crop?.name },
  ...yieldMetrics,
  ...(year === MULTI_YEAR
    ? [
        {
          displayString: `Max Yield ${startYear}-${startYear + years - 1}`,
          accessor: 'yieldByYear',
          format: (yieldByYear, { startYearIndex, endYearIndex, unit }) => {
            // clip year indices to be within the length of the array
            const includedYears = sliceYearArray(
              yieldByYear,
              startYearIndex,
              endYearIndex,
            );
            if (includedYears.length === 0) return asQuantity(0, { unit });
            return asQuantity(Math.max(...includedYears), { unit });
          },
        },
      ]
    : []),
  ...(year !== MULTI_YEAR
    ? [
        {
          displayString: `Yield for ${year}`,
          accessor: 'yieldByYear',
          format: (yieldByYear, { unit, startYearIndex }) => {
            if (startYearIndex < 0 || startYearIndex >= N_YEARS) {
              return asQuantity(0, { unit });
            }
            return asQuantity(yieldByYear[startYearIndex], { unit });
          },
        },
      ]
    : []),
];

const ExportDashboardYieldButton = ({ year, years, startYear, ...props }) => {
  const yieldUnitsMap = useUnitsMap(YIELD_UNITS_BY_DEFAULT);

  return (
    <ExportDashboardYearOrYearsButton
      columns={yieldColumns(year, years, startYear)}
      dataTransform={(farmData) =>
        getYieldData(yieldUnitsMap)(farmData).cropAggregates
      }
      query={yieldQuery}
      name="yield"
      year={year}
      years={years}
      startYear={startYear}
      {...props}
    />
  );
};

export default ExportDashboardYieldButton;
